/**
 * Mate constraint calculations — pure linear algebra.
 * Computes rigid transforms (rotation + translation) from two anchor points + normals.
 * Uses Three.js for quaternion/euler math.
 */
import * as THREE from "three";
import type { Vec3 } from "./featureTypes";

const RAD2DEG = 180 / Math.PI;

/** Round to 2 decimal places */
function r2(v: number): number { return Math.round(v * 100) / 100; }

/**
 * Compute the transform to align Part B's face onto Part A's face.
 * Result: the position and rotation to apply to Part B so that:
 *   - Part B's normal at targetPoint is anti-parallel to Part A's normal at sourcePoint
 *   - Part B's targetPoint coincides with Part A's sourcePoint
 *
 * "Face-to-face" means normals point at each other (anti-parallel).
 */
export function computeFaceAlignment(
  sourcePoint: Vec3,
  sourceNormal: Vec3,
  targetPoint: Vec3,
  targetNormal: Vec3,
): { position: Vec3; rotation: Vec3 } {
  const srcN = new THREE.Vector3(...sourceNormal).normalize();
  const tgtN = new THREE.Vector3(...targetNormal).normalize();

  // We want target normal to point OPPOSITE to source normal (face-to-face)
  const desiredN = srcN.clone().negate();

  // Compute rotation to align target normal → desired direction
  const quat = new THREE.Quaternion().setFromUnitVectors(tgtN, desiredN);

  // Convert to euler angles (degrees, XYZ order — matches scriptGenerator)
  const euler = new THREE.Euler().setFromQuaternion(quat, "XYZ");
  const rotation: Vec3 = [
    r2(euler.x * RAD2DEG),
    r2(euler.y * RAD2DEG),
    r2(euler.z * RAD2DEG),
  ];

  // Rotate the target point by the computed quaternion
  const rotatedTargetPoint = new THREE.Vector3(...targetPoint).applyQuaternion(quat);

  // Translation: move rotated target point to source point
  const position: Vec3 = [
    r2(sourcePoint[0] - rotatedTargetPoint.x),
    r2(sourcePoint[1] - rotatedTargetPoint.y),
    r2(sourcePoint[2] - rotatedTargetPoint.z),
  ];

  return { position, rotation };
}

/**
 * Compute the translation to make Part B's click point coincide with Part A's click point.
 * No rotation change — just translates.
 */
export function computeCoincident(
  sourcePoint: Vec3,
  targetPoint: Vec3,
): { position: Vec3 } {
  return {
    position: [
      r2(sourcePoint[0] - targetPoint[0]),
      r2(sourcePoint[1] - targetPoint[1]),
      r2(sourcePoint[2] - targetPoint[2]),
    ],
  };
}

const DEG2RAD = Math.PI / 180;

/**
 * Compute a revolute (hinge) mate.
 * Aligns Part B's face coaxially with Part A's face (normals parallel),
 * positions the target on the source point, then applies an additional
 * rotation of `angle` degrees about the source normal axis.
 */
export function computeRevolute(
  sourcePoint: Vec3,
  sourceNormal: Vec3,
  targetPoint: Vec3,
  targetNormal: Vec3,
  angle: number,
): { position: Vec3; rotation: Vec3 } {
  const srcN = new THREE.Vector3(...sourceNormal).normalize();
  const tgtN = new THREE.Vector3(...targetNormal).normalize();

  // Align target normal anti-parallel to source normal (face-to-face base alignment)
  const desiredN = srcN.clone().negate();
  const alignQuat = new THREE.Quaternion().setFromUnitVectors(tgtN, desiredN);

  // Additional rotation about the source normal axis by `angle` degrees
  const axisQuat = new THREE.Quaternion().setFromAxisAngle(srcN, angle * DEG2RAD);

  // Combined: first align, then rotate about axis
  const quat = axisQuat.multiply(alignQuat);

  const euler = new THREE.Euler().setFromQuaternion(quat, "XYZ");
  const rotation: Vec3 = [
    r2(euler.x * RAD2DEG),
    r2(euler.y * RAD2DEG),
    r2(euler.z * RAD2DEG),
  ];

  const rotatedTargetPoint = new THREE.Vector3(...targetPoint).applyQuaternion(quat);
  const position: Vec3 = [
    r2(sourcePoint[0] - rotatedTargetPoint.x),
    r2(sourcePoint[1] - rotatedTargetPoint.y),
    r2(sourcePoint[2] - rotatedTargetPoint.z),
  ];

  return { position, rotation };
}

/**
 * Compute a slider (prismatic) mate.
 * Aligns faces anti-parallel (like face-to-face), then shifts the position
 * by `offset` mm along the source normal direction.
 */
export function computeSlider(
  sourcePoint: Vec3,
  sourceNormal: Vec3,
  targetPoint: Vec3,
  targetNormal: Vec3,
  offset: number,
): { position: Vec3; rotation: Vec3 } {
  // Base alignment is identical to face-to-face
  const base = computeFaceAlignment(sourcePoint, sourceNormal, targetPoint, targetNormal);

  // Shift along the source normal by offset
  const srcN = new THREE.Vector3(...sourceNormal).normalize();
  const position: Vec3 = [
    r2(base.position[0] + srcN.x * offset),
    r2(base.position[1] + srcN.y * offset),
    r2(base.position[2] + srcN.z * offset),
  ];

  return { position, rotation: base.rotation };
}

